// src/pages/ebilling/ConsentPage.js
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Container,
  Box,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Stack,
} from "@mui/material";
import { api } from "../../api/api";
import currentDateTime from "../../util/Convert";

const EBillingConsentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const [customerId, setCustomerId] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otpRef, setOtpRef] = useState("");
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [showPointDetail, setShowPointDetail] = useState(false);

  useEffect(() => {
    const state = location.state;
    const params = new URLSearchParams(location.search);
    const customerIdFromUrl = params.get("customerId");
    const showPointDetailFromUrl = params.get("showPointDetail") === "true";
    if (state) {
      setPhoneNumber(state.phoneNumber || "");
      setOtpRef(state.otpRef || "");
      setEmail(state.email || "");
      setCustomerId(state.customerId || customerIdFromUrl || "");
      setFirstName(state.firstName || "");
      setLastName(state.lastName || "");
      setShowPointDetail(showPointDetailFromUrl);
      setInitialDataLoaded(true);
    } else {
      if (customerIdFromUrl) {
        setCustomerId(customerIdFromUrl);
      } else {
        setMessage("ไม่พบข้อมูล Customer ID กรุณาเข้าสู่ระบบใหม่");
        setMessageType("error");
      }
      setInitialDataLoaded(true);
    }
  }, [location.state, location.search]);

  // Fetch consent details from the API immediately when component mounts
  const {
    data: consentDetails,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["consentDetails"],
    queryFn: async () => {
      return api.getUncheckConsentDetails(
        customerId,
        "Individual",
        process.env.REACT_APP_E_CONSENT_CONSENT_ID,
        process.env.REACT_APP_E_CONSENT_COMPANY_ID,
        "consent_ebilling",
      );
    },
    enabled: initialDataLoaded, // Only run after initial data is loaded
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    retry: 3, // Retry 3 times if failed
    onError: (error) => {
      // Navigate to close page with error when getUncheckConsentDetails fails
      navigate(
        `/close?customerId=${customerId}&status=false&msgErr=${encodeURIComponent(error.message || "เกิดข้อผิดพลาดในการโหลดข้อมูล")}`,
      );
    },
  });

  // Refetch when phoneNumber is set
  useEffect(() => {
    if (phoneNumber && initialDataLoaded) {
      refetch();
    }
  }, [phoneNumber, initialDataLoaded, refetch]);

  const handleScroll = (e) => {
    const element = e.target;
    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight;
    const clientHeight = element.clientHeight;

    // คำนวณเปอร์เซ็นต์
    //const scrollableHeight = scrollHeight - clientHeight;
    //const percentage = (scrollTop / scrollableHeight) * 100;
    // ตรวจสอบว่าอยู่ด้านบนหรือไม่
    //const isAtTop = scrollTop === 0;
    // ตรวจสอบว่าอยู่ด้านล่างหรือไม่ (ใช้ tolerance 5px)
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 5;
    // ตรวจสอบว่าใกล้ด้านล่างหรือไม่
    //const isNearBottom = percentage >= 90;

    setIsAtBottom(isAtBottom);

    // อัปเดต hasScrolledToBottom เมื่อ user เลื่อนถึงด้านล่าง
    if (isAtBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  };

  // Mutation for inserting consent
  const insertConsentMutation = useMutation({
    mutationFn: api.acceptConsent,
    onSuccess: (data) => {
      setMessage("บันทึกความยินยอมสำเร็จ");
      setMessageType("success");
      setTimeout(() => {
        setMessage("ยินยอมสำเร็จ! การดำเนินการ e-consent เสร็จสมบูรณ์");
        setMessageType("success");
        // Navigate to term page with success status
        setTimeout(() => {
          if (showPointDetail) {
            navigate(`/term?customerId=${customerId}&status=true`);
          } else {
            navigate(`/close?customerId=${customerId}&status=true`);
          }
        }, 1000);
      }, 500);
    },
    onError: (error) => {
      setMessage("เกิดข้อผิดพลาดในการบันทึกความยินยอม: " + error.message);
      setMessageType("error");
      // Navigate to close page with error status
      setTimeout(() => {
        navigate(
          `/close?customerId=${customerId}&status=false&msgErr=${encodeURIComponent(error.message || "เกิดข้อผิดพลาดในการโหลดข้อมูล")}`,
        );
      }, 2000);
    },
  });

  const handleConfirmConsent = async () => {
    // ตรวจสอบว่าผู้ใช้เลื่อนอ่านจนจบหรือยัง
    if (!isAtBottom) {
      setMessage("กรุณาเลื่อนอ่านเนื้อหาจนถึงจุดสุดท้ายก่อนยืนยันความยินยอม");
      setMessageType("warning");
      return;
    }

    setMessage("");
    setMessageType("info");

    // Accept all available consents automatically
    if (!consentDetails?.data || consentDetails.data.length === 0) {
      setMessage("ไม่พบข้อมูลความยินยอม");
      setMessageType("error");
      return;
    }

    const customerName =
      firstName && lastName ? `${firstName} ${lastName}` : "Unknown Customer";

    // Get all consent IDs and accept them all
    const consentIds = consentDetails.data
      .map((item) => {
        const numericId = parseInt(item.id, 10);
        if (isNaN(numericId)) {
          return null;
        }
        return numericId;
      })
      .filter((id) => id !== null);

    if (consentIds.length === 0) {
      setMessage("เกิดข้อผิดพลาดในการประมวลผลรายการความยินยอม");
      setMessageType("error");
      return;
    }

    try {
      await insertConsentMutation.mutateAsync({
        consent_id: consentIds,
        subject: customerId,
        subject_type: "Verify email otp consent ebilling",
        customer_source: "Customer App",
        customer_name: customerName,
        source: "consent_ebilling",
        company_id: process.env.REACT_APP_E_CONSENT_COMPANY_ID,
        signed: {
          aal: otpRef,
          aal_dt: currentDateTime,
          mobile: phoneNumber,
          customer_name: customerName,
          email: email,
          signing_dt: currentDateTime,
        },
      });
    } catch (error) {
      navigate(
        `/close?customerId=${customerId}&status=false&msgErr=${encodeURIComponent(error.message || "เกิดข้อผิดพลาดในการสร้างข้อมูล")}`,
      );
    }
  };

  const isConfirmDisabled =
    insertConsentMutation.isPending ||
    !consentDetails?.data ||
    consentDetails.data.length === 0 ||
    !isAtBottom;

  // Show loading state
  if (isLoading) {
    return (
      <Container
        component="main"
        maxWidth="sm"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          p: 2,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            width: "100%",
            maxWidth: 450,
            minHeight: 600,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            backgroundColor: "white",
            border: "1px solid #e0e0e0",

            p: 3,
          }}
        >
          <CircularProgress size={40} />
          <Typography variant="body1" sx={{ mt: 2, color: "#666" }}>
            กำลังโหลดข้อมูล...
          </Typography>
        </Paper>
      </Container>
    );
  }

  // Show error state
  if (isError) {
    return (
      <Container
        component="main"
        maxWidth="sm"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          p: 2,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            width: "100%",
            maxWidth: 450,
            minHeight: 600,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            backgroundColor: "white",
            border: "1px solid #e0e0e0",
            p: 3,
          }}
        >
          <Alert severity="error" sx={{ mb: 2 }}>
            เกิดข้อผิดพลาดในการโหลดข้อมูล:{" "}
            {error?.message || "กรุณาลองอีกครั้ง"}
          </Alert>
        </Paper>
      </Container>
    );
  }

  return (
    <Container
      component="main"
      maxWidth="sm"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        p: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          width: "100%",
          maxWidth: 450,
          minHeight: 800,
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          overflow: "hidden",
          backgroundColor: "white",
        }}
      >
        {/* Logo and Header Section */}
        <Box
          sx={{
            width: "100%",
            bgcolor: "white",
            p: 3,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <Box
            component="img"
            src="/image/logo-header.png"
            alt="SG CAPITAL"
            sx={{
              height: 60,
              mb: 2,
              objectFit: "contain",
            }}
          />
          <Typography
            variant="h5"
            component="h1"
            sx={{
              fontWeight: 500,
              color: "#666666",
              fontSize: "1.5rem",
            }}
          >
            Verify Email
          </Typography>
        </Box>

        {/* Form Content - Takes remaining space */}
        <Box
          sx={{
            p: 5,
            pt: 2, // Reduced top padding to move title up
            width: "100%",
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Title - Moved up by reducing top spacing */}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "#333",
              fontSize: "1.3rem",
              textAlign: "center",
              mt: -1, // Move title up by 1cm equivalent (approximately -1 in Material-UI spacing)
              mb: 2,
            }}
          >
            การยินยอมรับเอกสารทางอิเล็กทรอนิกส์
          </Typography>

          {/* Content Area - Grows to fill space */}
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <Stack spacing={3} sx={{ width: "100%", flex: 1 }}>
              {/* Consent Content */}
              <Box
                sx={{
                  flex: 1,
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  overflow: "hidden",
                  backgroundColor: "#f8f9fa",
                }}
              >
                <Box
                  onScroll={handleScroll}
                  sx={{
                    height: 380,
                    overflowY: "auto",
                    p: 3,
                    fontSize: "0.95rem",
                    lineHeight: 1.6,
                    color: "#333",
                    "&::-webkit-scrollbar": {
                      width: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "#f1f1f1",
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#c1c1c1",
                      borderRadius: "4px",
                      "&:hover": {
                        backgroundColor: "#a8a8a8",
                      },
                    },
                  }}
                >
                  {consentDetails?.data?.map((consentItem, index) => (
                    <div
                      key={consentItem.id}
                      dangerouslySetInnerHTML={{
                        __html: consentItem.fullContent,
                      }}
                      style={{
                        marginBottom:
                          index < consentDetails.data.length - 1 ? "20px" : "0",
                      }}
                    />
                  ))}
                </Box>
              </Box>

              {/* Message Alert */}
              {message && (
                <Box sx={{ width: "100%" }}>
                  <Alert severity={messageType} sx={{ fontSize: "0.9rem" }}>
                    {message}
                  </Alert>
                </Box>
              )}
            </Stack>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ mt: 3 }}>
            {/* Confirm Button */}
            <Button
              onClick={handleConfirmConsent}
              fullWidth
              variant="contained"
              disabled={isConfirmDisabled}
              sx={{
                mb: 2,
                py: 2.5,
                borderRadius: 3,
                fontSize: "1.2rem",
                fontWeight: 500,
                textTransform: "none",
                backgroundColor: isConfirmDisabled ? "#e0e0e0" : "#1976d2",
                color: isConfirmDisabled ? "#a0a0a0" : "white",
                "&:hover": {
                  backgroundColor: isConfirmDisabled ? "#e0e0e0" : "#1565c0",
                },
                "&:disabled": {
                  backgroundColor: "#e0e0e0",
                  color: "#a0a0a0",
                },
              }}
            >
              {insertConsentMutation.isPending ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1, color: "white" }} />
                  กำลังดำเนินการ...
                </>
              ) : (
                "ยินยอม"
              )}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default EBillingConsentPage;
